import React, { useEffect, useState } from "react";
import api, { formatErrorDetail, formatDateTime } from "@/lib/api";
import { PageHeader, Surface, TableWrap, Empty, Stat } from "@/components/app/Primitives";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const CATEGORIES = ["handbook", "policy", "sop", "benefits", "form", "memo", "training", "faq", "other"];

export default function AIAdmin() {
    const { user } = useAuth();
    const isSuper = (user?.roles || []).some((r) => r.role === "super_admin");
    const [tab, setTab] = useState("analytics");
    const [analytics, setAnalytics] = useState(null);
    const [docs, setDocs] = useState([]);
    const [license, setLicense] = useState(null);
    const [convos, setConvos] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ title: "", category: "policy", tags: "", effective_date: "", content: "" });
    const [lic, setLic] = useState({ enabled: false, plan: "trial", expires_at: "", seats: "" });
    const [viewConvo, setViewConvo] = useState(null);
    const [viewDoc, setViewDoc] = useState(null);

    const openDoc = async (id) => {
        try { const { data } = await api.get(`/ai/admin/kb/${id}`); setViewDoc(data); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Couldn't open document"); }
    };

    const loadAll = async () => {
        try {
            const [a, d, l, c] = await Promise.all([
                api.get("/ai/admin/analytics"), api.get("/ai/admin/kb"),
                api.get("/ai/admin/license"), api.get("/ai/admin/conversations"),
            ]);
            setAnalytics(a.data); setDocs(d.data.items || []); setLicense(l.data); setConvos(c.data.items || []);
            setLic({ enabled: !!l.data.enabled, plan: l.data.plan || "trial", expires_at: l.data.expires_at || "", seats: l.data.seats || "" });
        } catch (e) {
            if (e.response?.status === 404) toast.error("AI HR Agent module is not enabled for this company.");
            else toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed to load");
        }
    };
    useEffect(() => { loadAll(); }, []);

    const saveDoc = async () => {
        if (!form.title.trim() || !form.content.trim()) return toast.error("Title and content are required");
        try {
            await api.post("/ai/admin/kb", { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) });
            toast.success("Document added to knowledge base");
            setOpen(false); setForm({ title: "", category: "policy", tags: "", effective_date: "", content: "" });
            loadAll();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    const archiveDoc = async (id) => {
        try { await api.post(`/ai/admin/kb/${id}/archive`); toast.success("Archived"); loadAll(); }
        catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    const saveLicense = async () => {
        try {
            await api.put("/ai/admin/license", { ...lic, seats: lic.seats === "" ? null : Number(lic.seats), expires_at: lic.expires_at || null });
            toast.success("License updated"); loadAll();
        } catch (e) { toast.error(formatErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    return (
        <div>
            <PageHeader title="AI HR Agent — Administration" subtitle="Knowledge base, usage analytics, licensing and conversation history for the premium AI assistant." />
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="analytics" data-testid="ai-tab-analytics">Analytics</TabsTrigger>
                    <TabsTrigger value="kb" data-testid="ai-tab-kb">Knowledge Base</TabsTrigger>
                    <TabsTrigger value="license" data-testid="ai-tab-license">License</TabsTrigger>
                    <TabsTrigger value="conversations" data-testid="ai-tab-convos">Conversations</TabsTrigger>
                </TabsList>

                <TabsContent value="analytics">
                    {analytics && (
                        <>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 my-3">
                                <Stat label="Conversations" value={analytics.conversations} />
                                <Stat label="Resolution Rate" value={`${Math.round((analytics.resolution_rate || 0) * 100)}%`} tone="primary" />
                                <Stat label="Ticket Candidates" value={analytics.ticket_conversion_candidates} />
                                <Stat label="AI Requests" value={analytics.usage?.requests || 0} />
                            </div>
                            <Surface className="p-4 mb-3">
                                <h3 className="font-semibold text-sm mb-2">Questions by Intent</h3>
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(analytics.by_intent || {}).map(([k, v]) => <span key={k} className="text-xs px-2 py-1 rounded bg-secondary">{k}: {v}</span>)}
                                    {!Object.keys(analytics.by_intent || {}).length && <span className="text-xs text-muted-foreground">No conversations yet.</span>}
                                </div>
                            </Surface>
                            <Surface className="p-4">
                                <h3 className="font-semibold text-sm mb-2">Knowledge Gaps (unresolved questions)</h3>
                                {(analytics.knowledge_gaps || []).length ? (
                                    <ul className="text-sm space-y-1">{analytics.knowledge_gaps.map((q, i) => <li key={i} className="text-muted-foreground">• {q}</li>)}</ul>
                                ) : <div className="text-xs text-muted-foreground">No knowledge gaps — every question was answered.</div>}
                            </Surface>
                        </>
                    )}
                </TabsContent>

                <TabsContent value="kb">
                    <div className="flex justify-end my-2">
                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <button className="h-9 px-3 rounded-md bg-primary text-white text-sm flex items-center gap-2" data-testid="ai-kb-add"><Plus className="size-4" /> Add Document</button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                                <DialogHeader><DialogTitle>Add Knowledge Base Document</DialogTitle></DialogHeader>
                                <div className="space-y-2">
                                    <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="ai-kb-title" /></div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div><Label>Category</Label>
                                            <select className="w-full h-10 px-3 rounded-md border border-border" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                                                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </div>
                                        <div><Label>Effective Date</Label><Input type="date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} /></div>
                                    </div>
                                    <div><Label>Tags (comma-separated)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="leave, vacation, encashment" /></div>
                                    <div><Label>Content</Label><textarea className="w-full p-2 rounded-md border border-border text-sm" rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="ai-kb-content" placeholder="Paste the policy / handbook text the AI should answer from." /></div>
                                </div>
                                <DialogFooter><button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={saveDoc} data-testid="ai-kb-save">Save Document</button></DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                    <TableWrap>
                        <thead><tr><th>Title</th><th>Category</th><th className="num">Version</th><th>Effective</th><th>Tags</th><th></th></tr></thead>
                        <tbody>
                            {docs.map((d) => (
                                <tr key={d.id} data-testid={`ai-kb-row-${d.id}`}>
                                    <td className="font-medium">{d.title}</td>
                                    <td className="capitalize">{d.category}</td>
                                    <td className="num">v{d.version}</td>
                                    <td className="font-mono text-xs">{d.effective_date || "—"}</td>
                                    <td className="text-xs">{(d.tags || []).join(", ") || "—"}</td>
                                    <td>
                                        <div className="flex items-center gap-2">
                                            <button className="text-xs text-primary" onClick={() => openDoc(d.id)} data-testid={`ai-kb-view-${d.id}`}>View</button>
                                            <button className="text-xs text-destructive" onClick={() => archiveDoc(d.id)}>Archive</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {!docs.length && <tr><td colSpan={6}><Empty title="No documents yet" subtitle="Add policies and handbooks the AI should answer from." /></td></tr>}
                        </tbody>
                    </TableWrap>
                </TabsContent>

                <TabsContent value="license">
                    <Surface className="p-5 mt-2 max-w-lg space-y-3">
                        <div className="text-sm text-muted-foreground">Premium licensing for the AI HR Agent. Only the global super admin can change activation.</div>
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" disabled={!isSuper} checked={lic.enabled} onChange={(e) => setLic({ ...lic, enabled: e.target.checked })} data-testid="ai-lic-enabled" /> License active</label>
                        <div><Label>Plan</Label>
                            <select className="w-full h-10 px-3 rounded-md border border-border" disabled={!isSuper} value={lic.plan} onChange={(e) => setLic({ ...lic, plan: e.target.value })}>
                                <option value="trial">Trial</option><option value="standard">Standard</option><option value="enterprise">Enterprise</option>
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label>Expires</Label><Input type="date" disabled={!isSuper} value={lic.expires_at ? String(lic.expires_at).slice(0, 10) : ""} onChange={(e) => setLic({ ...lic, expires_at: e.target.value })} /></div>
                            <div><Label>Seats (blank = unlimited)</Label><Input type="number" disabled={!isSuper} value={lic.seats} onChange={(e) => setLic({ ...lic, seats: e.target.value })} /></div>
                        </div>
                        <div className="text-xs text-muted-foreground border-t pt-3">
                            Usage — requests: <b>{license?.usage?.requests || 0}</b> · conversations: <b>{license?.usage?.conversations || 0}</b> · tokens: <b>{license?.usage?.tokens || 0}</b>
                        </div>
                        {isSuper && <button className="h-9 px-3 rounded-md bg-primary text-white text-sm" onClick={saveLicense} data-testid="ai-lic-save">Save License</button>}
                    </Surface>
                </TabsContent>

                <TabsContent value="conversations">
                    <TableWrap>
                        <thead><tr><th>When</th><th>Intent</th><th>Question</th><th>Resolved</th><th>LLM</th></tr></thead>
                        <tbody>
                            {convos.map((c) => (
                                <tr key={c.id} className="cursor-pointer" onClick={() => setViewConvo(c)} data-testid={`ai-convo-${c.id}`}>
                                    <td className="font-mono text-xs">{formatDateTime(c.created_at)}</td>
                                    <td>{c.intent}</td>
                                    <td className="max-w-md truncate">{c.question}</td>
                                    <td>{c.resolved ? <span className="badge badge-approved">yes</span> : <span className="badge badge-rejected">no</span>}</td>
                                    <td>{c.used_llm ? "yes" : "grounded"}</td>
                                </tr>
                            ))}
                            {!convos.length && <tr><td colSpan={5}><Empty title="No conversations yet" /></td></tr>}
                        </tbody>
                    </TableWrap>
                </TabsContent>
            </Tabs>

            {/* Conversation detail */}
            <Dialog open={!!viewConvo} onOpenChange={(o) => !o && setViewConvo(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>Conversation</DialogTitle></DialogHeader>
                    {viewConvo && (
                        <div className="space-y-3 max-h-[65vh] overflow-auto text-sm">
                            <div className="text-xs text-muted-foreground">{formatDateTime(viewConvo.created_at)} · intent: {viewConvo.intent} · {viewConvo.resolved ? "resolved" : "unresolved"} · {viewConvo.used_llm ? "LLM" : "grounded"}</div>
                            <div><div className="text-xs uppercase font-semibold text-muted-foreground mb-1">Question</div><div className="rounded-md bg-secondary p-3">{viewConvo.question}</div></div>
                            <div><div className="text-xs uppercase font-semibold text-muted-foreground mb-1">Answer</div><div className="rounded-md border border-border p-3 whitespace-pre-wrap">{viewConvo.answer}</div></div>
                            {!!(viewConvo.citations || []).length && (
                                <div><div className="text-xs uppercase font-semibold text-muted-foreground mb-1">Sources</div>
                                    {viewConvo.citations.map((c) => <div key={c.document_id} className="text-xs text-muted-foreground">• {c.title} v{c.version}{c.effective_date ? ` · eff. ${c.effective_date}` : ""}</div>)}
                                </div>
                            )}
                            {viewConvo.converted_to_ticket && <div className="text-xs text-emerald-600">Converted to ticket.</div>}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Knowledge base document viewer */}
            <Dialog open={!!viewDoc} onOpenChange={(o) => !o && setViewDoc(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>{viewDoc?.title}</DialogTitle></DialogHeader>
                    {viewDoc && (
                        <div className="space-y-3 max-h-[65vh] overflow-auto text-sm">
                            <div className="text-xs text-muted-foreground capitalize">{viewDoc.category} · v{viewDoc.version}{viewDoc.effective_date ? ` · effective ${viewDoc.effective_date}` : ""}{(viewDoc.tags || []).length ? ` · ${viewDoc.tags.join(", ")}` : ""}</div>
                            <div className="rounded-md bg-secondary p-3 whitespace-pre-wrap leading-relaxed">{viewDoc.content}</div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
